import React, { useState } from "react";
import { SpeakerDistributionDiagram, SpeakerDistributionItem } from "./SpeakerDistributionDiagram";
import { useUi } from "@jelly/lesson";

import { messages } from "../Case1TechnicalPov.messages";
import { useIntl, MessageDescriptor } from "react-intl";
import { PhotoView } from "@src/lms/jelly/extensions/layouts/photo-view/PhotoView";
import * as appConfig from "appConfig";

import styles from "./SpeakerDistributionSlide.module.scss";

interface ModalData {
  title: MessageDescriptor;
  description: MessageDescriptor;
  imgDescription: MessageDescriptor;
  imgSrc: string;
}

const MODAL_DATA: Record<SpeakerDistributionItem, ModalData> = {
  "front-system": {
    title: messages.modalSpeakerFrontTitle,
    description: messages.modalSpeakerFrontDescription,
    imgDescription: messages.modalSpeakerFrontImgInfo,
    imgSrc: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/speaker/front.png`,
  },
  "ceiling-system": {
    title: messages.modalSpeakerCeilingTitle,
    description: messages.modalSpeakerCeilingDescription,
    imgDescription: messages.modalSpeakerCeilingImgInfo,
    imgSrc: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/speaker/ceiling.png`,
  },
  "front-system-sub-bass": {
    title: messages.modalSpeakerFrontbassTitle,
    description: messages.modalSpeakerFrontbassDescription,
    imgDescription: messages.modalSpeakerFrontbassImgInfo,
    imgSrc: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/speaker/front-bass.png`,
  },
  "ceiling-system-sub-bass": {
    title: messages.modalSpeakerCeilingbassTitle,
    description: messages.modalSpeakerCeilingbassDescription,
    imgDescription: messages.modalSpeakerCeilingbassImgInfo,
    imgSrc: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/speaker/ceiling-bass.png`,
  },
  "surround-system-sides": {
    title: messages.modalSpeakerSurroundsideTitle,
    description: messages.modalSpeakerSurroundsideDescription,
    imgDescription: messages.modalSpeakerSurroundsideImgInfo,
    imgSrc: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/speaker/surround-sides.png`,
  },
  "surround-system-back": {
    title: messages.modalSpeakerSurroundbackTitle,
    description: messages.modalSpeakerSurroundbackDescription,
    imgDescription: messages.modalSpeakerSurroundbackImgInfo,
    imgSrc: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/technical-pov/speaker/surround-back.png`,
  },
} as const;

export const SpeakerDistributionSlide: React.FC = () => {
  const [activeItem, setActiveItem] = useState<SpeakerDistributionItem | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const closePopup = (): void => setModalOpen((open) => !open);
  const ui = useUi();
  const handleItemClick = (item: SpeakerDistributionItem): void => {
    setActiveItem(item);
    setModalOpen(true);
  };

  const activeItemData = MODAL_DATA[activeItem ?? "front-system"];
  return (
    <>
      <SpeakerDistributionDiagram
        onItemSelect={handleItemClick}
        activeItems={modalOpen && activeItem ? [activeItem] : []}
      />
      <ui.Modal
        visible={modalOpen}
        onClose={closePopup}
        title={formatMessage(activeItemData.title)}
        className={styles.modal}
      >
        <div className={styles.content}>
          <p>{formatMessage(activeItemData.description)}</p>
          <PhotoView imgSrc={activeItemData.imgSrc} description={formatMessage(activeItemData.imgDescription)} />
        </div>
      </ui.Modal>
    </>
  );
};
